import {inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {baseUrl} from "../../../../core/constants/base-url";
import {Observable} from "rxjs";
import {
  ReleaseCalendarQueryParams,
  ReleaseCalendarData,
  releaseCalendarsResourceType,
  ReleaseCalendarSelectedOptions,
  LimitCheckParams,
  ReleaseCalendarProduct, UpdateGroupStylesParams
} from "../types/release-calendar-types";
import {UpdatePositionProductsParams} from "../types/release-calendar-arrow.types";
import { ReleaseCalendarApiService } from "./release-calendar-api.service";
import { baseBottomOffset, baseMobileBottomOffset, months } from "../configs/release-calendar-config";
import { DEFAULT_ZOOM } from "../../../../core/constants/constants";

@Injectable({
  providedIn: 'root'
})
export class ReleaseCalendarService {
  private releaseCalendarApiService: ReleaseCalendarApiService = inject(ReleaseCalendarApiService);

  public getReleaseCalendarData(queryParams: ReleaseCalendarQueryParams): Promise<ReleaseCalendarData> {
    return new Promise((resolve, reject) => {
      this.releaseCalendarApiService.fetchReleaseCalendar(queryParams).subscribe({
        next: (releaseCalendarData: ReleaseCalendarData) => resolve(releaseCalendarData),
        error: (error) => reject(error)
      });
    });
  }

  public generateDays(releaseCalendarSelectedOptions: ReleaseCalendarSelectedOptions): Array<number> {
    const { month, year } = releaseCalendarSelectedOptions;
    const adjustedMonth = Number(month) - 1;
    const firstDay = new Date(Number(year), adjustedMonth, 1).getDay();
    const totalDaysInMonth = new Date(Number(year), adjustedMonth + 1, 0).getDate();

    const offsetStart = firstDay === 0 ? 6 : firstDay - 1;
    const totalCells = offsetStart + totalDaysInMonth;
    const rows = Math.ceil(totalCells / 7) * 7;

    return Array.from({ length: rows }, (_, i) =>
      i >= offsetStart && i < offsetStart + totalDaysInMonth ? i - offsetStart + 1 : 0
    );
  }

  public isLimitExceeded(limitCheckParams: LimitCheckParams): boolean {
    const { price, plannedPrice, planFactExceptionLimit } = limitCheckParams;
    if (plannedPrice === undefined || plannedPrice === null) {
      return !!price;
    }

    if (planFactExceptionLimit) {
      return price > planFactExceptionLimit;
    }

    return price > plannedPrice;
  }

  public updatePositionProducts(updatePositionProducts: UpdatePositionProductsParams): Array<ReleaseCalendarProduct> {
    const { products, direction, productIndex } = updatePositionProducts;
    if (direction === 'up' && productIndex > 0) {
      [products[productIndex - 1], products[productIndex]] =
        [products[productIndex], products[productIndex - 1]];
    }

    if (direction === 'down' && productIndex < products.length - 1) {
      [products[productIndex], products[productIndex + 1]] =
        [products[productIndex + 1], products[productIndex]];
    }

    return products;
  }

  public getDefaultReleaseCalendarSelectedOptions(): ReleaseCalendarSelectedOptions {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth() + 1;
    const currentMonthName = months.find(month => month.value === currentMonth)?.label || '';

    return {
      year: currentDate.getFullYear(),
      month: currentMonth,
      monthName: currentMonthName
    };
  }

  public updateGroupStyles(params: UpdateGroupStylesParams): Record<string, string> {
    const { currentZoomOption, isMobile } = params;
    const scaleFactor = 100 / currentZoomOption;
    const scaledWidthHeight = 100 * scaleFactor;
    const bottomOffset = isMobile ? baseMobileBottomOffset : baseBottomOffset;
    const height = `calc(${scaledWidthHeight}vh - ${bottomOffset * scaleFactor}px)`;

    return {
      transform: `scale(${currentZoomOption / 100})`,
      width: `${scaledWidthHeight}%`,
      minHeight: currentZoomOption <= DEFAULT_ZOOM ? height : '',
      maxHeight: currentZoomOption > DEFAULT_ZOOM ? height : ''
    };
  }

  public getReleaseCalendarQueryParams(releaseCalendarSelectedOptions: ReleaseCalendarSelectedOptions | null): ReleaseCalendarQueryParams {
    return {
      month: releaseCalendarSelectedOptions?.month || new Date().getMonth()  + 1,
      year: releaseCalendarSelectedOptions?.year || new Date().getFullYear(),
      showAllTeams: true
    }
  }
}
